import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { Link } from 'react-router-dom';
import { fetchAllDataAPI, addFormData } from '../utils/api';
import { toast } from 'react-toastify';
import { FaFilter, FaRedo, FaSearch, FaEye } from 'react-icons/fa';
import { numberFormat } from '../components/numberFormat';
import { CiExport } from 'react-icons/ci';
import CsvDownloader from 'react-csv-downloader';
import withAuth from '../utils/withAuth';

const STATUS_STYLE = {
    'Active': { color: '#0e7c52', bg: '#e8f5e9', dot: '#0e7c52' },
    'Ending Soon': { color: '#e65100', bg: '#fff3e0', dot: '#ff9800' },
    'Drop-off': { color: '#c62828', bg: '#ffebee', dot: '#c62828' },
};

const STAGE_STYLE = {
    'First Time Buyer': { color: '#1565c0', bg: '#e3f2fd' },
    'Early Repeat': { color: '#6a1b9a', bg: '#f3e5f5' },
    'Consistent': { color: '#0e7c52', bg: '#e8f5e9' },
    'Loyal/VIP': { color: '#b8860b', bg: '#fff8e1' },
    'No Delivery': { color: '#c62828', bg: '#ffebee' },
};

// ✅ Persist filters in sessionStorage so they survive back navigation
const FILTER_KEY = 'retention_filters';
const SEARCH_KEY = 'retention_search';
const PAGE_KEY = 'retention_page';

const getSaved = (key, def) => {
    try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : def; }
    catch { return def; }
};
const setSaved = (key, val) => {
    try { sessionStorage.setItem(key, JSON.stringify(val)); } catch { }
};

const SkeletonRow = () => (
    <tr>{[...Array(15)].map((_, i) => (
        <td key={i}><div style={{ height: 14, borderRadius: 6, background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} /></td>
    ))}</tr>
);

const CATEGORY_PRODUCT_MAP = {
    'Joint Pain': ['Dhurandar Oil', 'Ortho Veda Oil'],
    'Diabetes': ['Ayush 82', 'Dr Madhu Amrit Kit'],
    'Addiction': ['Addiction killer Powder', 'Addiction killer Liquid', 'Nasha Free'],
    'Sexual Health': [
        'Liv Muztang REX',
        'Liv Muztang Capsules',
        'Jaam-e-ishq',
        'Ultimate Hammer',
        'Kaama Gold',
        'Sandy RX',
        'Herbo 365',
        'Macamo, the Latin Lava',
        'Ayush for Men',
        'Liv Muztang Healthy Shots'
    ],
    'Immunity': ['Ayush Kavach', 'Ayush Kwath'],
    'Piles Management': ['Dr Piles free'],
    'Hair Care': ['Aadved Adivasi Hair Oil'],
    'Digestion': ['Vajra 44', 'Gut Raksha'],
    'Weight Management': ['Poshan Plus', 'Aadved Slim Herbs', 'Power Rootz SSS Formula'],
    'Others': [
        'Aadved Sleep',
        'Power Rootz Keeda Jadi',
        'Vayu Shuddhi',
        'Liver Care',
        'Saumya Plus',
        'Stones Veda',
        'Sanjeev Ras'
    ],
};

const normalizeText = (value = '') =>
    String(value).toLowerCase().replace(/\s+/g, ' ').trim();

const getCategoryByProduct = (productName = '', variant = '') => {
    const normalizedProduct = normalizeText(`${productName} ${variant}`);

    for (const [category, products] of Object.entries(CATEGORY_PRODUCT_MAP)) {
        const found = products.some(p => {
            const normalizedMappedProduct = normalizeText(p);
            return normalizedProduct.includes(normalizedMappedProduct);
        });

        if (found) return category;
    }

    return 'Others';
};

const RetentionPage = () => {
    const ref = useRef();

    // Restore filters from sessionStorage on mount
    const [data, setData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => getSaved(FILTER_KEY, {}));
    const [search, setSearch] = useState(() => getSaved(SEARCH_KEY, ''));
    const [currentPage, setPage] = useState(() => getSaved(PAGE_KEY, 1));

    const perPage = 100;

    // FIX: Client-side filtering includes date range + delivery status
    const filtered = data.filter(r => {
        if (search) {
            const s = search.toLowerCase();
            const mobile = String(r.mobile || '');
            const orderNumber = String(r.last_order_number || '');
            const name = String(r.name || '').toLowerCase();
            const product = String(r.last_product || '').toLowerCase();

            const match =
                mobile.includes(search) ||
                orderNumber.includes(search) ||
                name.includes(s) ||
                product.includes(s);

            if (!match) return false;
        }

        // Product-based health category filter
        if (filters.health_category) {
            const selectedProducts = CATEGORY_PRODUCT_MAP[filters.health_category] || [];
            const currentProduct = normalizeText(r.last_product);

            const productMatched = selectedProducts.some(productName => {
                const normalizedMappedProduct = normalizeText(productName);

                return (
                    currentProduct.includes(normalizedMappedProduct) ||
                    normalizedMappedProduct.includes(currentProduct)
                );
            });

            if (!productMatched) return false;
        }

        // Date range filter
        if (filters.date_from && r.last_order_date < filters.date_from) return false;
        if (filters.date_to && r.last_order_date > filters.date_to) return false;

        // Delivery filter
        if (filters.delivery_status === 'delivered' && (r.successful_orders || 0) === 0) return false;
        if (filters.delivery_status === 'not_delivered' && (r.successful_orders || 0) > 0) return false;

        return true;
    });

    const npage = Math.ceil(filtered.length / perPage);
    const records = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
    const numbers = [...Array(npage + 1).keys()].slice(1);

    const stats = {
        total: data.length,
        active: data.filter(r => r.treatment_status === 'Active').length,
        endingSoon: data.filter(r => r.treatment_status === 'Ending Soon').length,
        dropoff: data.filter(r => r.treatment_status === 'Drop-off').length,
        vip: data.filter(r => r.purchase_stage === 'Loyal/VIP').length,
        totalLTV: data.reduce((s, r) => s + (r.ltv || 0), 0),
    };

    // Save filters/search/page to sessionStorage whenever they change
    useEffect(() => { setSaved(FILTER_KEY, filters); }, [filters]);
    useEffect(() => { setSaved(SEARCH_KEY, search); }, [search]);
    useEffect(() => { setSaved(PAGE_KEY, currentPage); }, [currentPage]);

    useEffect(() => { loadData(filters); }, []);

    const loadData = (activeFilters = {}) => {
        setLoading(true);
        // Check if any server-side filters are active
        const serverFilters = {};
        ['purchase_stage', 'treatment_status', 'state', 'language', 'search'].forEach(k => {
            if (activeFilters[k]) serverFilters[k] = activeFilters[k];
        });

        const hasServerFilters = Object.keys(serverFilters).length > 0;

        const apiCall = hasServerFilters
            ? addFormData('sub-agent-retention-filter', serverFilters)
            : fetchAllDataAPI('sub-agent-retention-data');

        apiCall
            .then(res => {
                const d = res?.data;
                if (d?.status === 1) {
                    const rawData = d.data || [];
                    const uniqueData = [];
                    const seen = new Set();
                    rawData.forEach(r => {
                        const key = `${r.mobile}_${r.last_order_date}`;
                        if (!seen.has(key)) {
                            seen.add(key);
                            uniqueData.push(r);
                        }
                    });
                    setData(uniqueData);
                }
                else toast.warning('No retention data found');
            })
            .catch(() => toast.error('Failed to load retention data'))
            .finally(() => setLoading(false));
    };

    const handleFilter = (e) => {
        e.preventDefault();
        setPage(1);
        loadData(filters);
    };

    const reset = () => {
        ref.current.reset();
        const empty = {};
        setFilters(empty);
        setSearch('');
        setPage(1);
        setSaved(FILTER_KEY, empty);
        setSaved(SEARCH_KEY, '');
        setSaved(PAGE_KEY, 1);
        loadData({});
    };

    const handleInput = e => {
        const updated = { ...filters, [e.target.name]: e.target.value };
        setFilters(updated);
    };

    const csvColumns = [
        { id: 'last_order_number', displayName: 'Order No' },
        { id: 'name', displayName: 'Name' },
        { id: 'mobile', displayName: 'Mobile' },
        { id: 'state', displayName: 'State' },
        { id: 'language', displayName: 'Language' },
        { id: 'health_category', displayName: 'Health Category' },
        { id: 'last_product', displayName: 'Product' },
        { id: 'last_variant', displayName: 'Pack Variant' },
        { id: 'pack_label', displayName: 'Pack' },
        { id: 'order_count', displayName: 'Orders' },
        { id: 'successful_orders', displayName: 'Delivered' },
        { id: 'ltv', displayName: 'LTV' },
        { id: 'last_order_date', displayName: 'Last Order' },
        { id: 'refill_date', displayName: 'Refill Date' },
        { id: 'days_remaining', displayName: 'Days Left' },
        { id: 'treatment_status', displayName: 'Status' },
        { id: 'purchase_stage', displayName: 'Stage' },
    ];

    return (
        <div className="misDashboard">
            <Helmet><title>SATKARTAR::RETENTION</title></Helmet>
            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes fadeInUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.03)} }
                .retention-page { animation: fadeInUp 0.4s ease; }
                .ret-stats { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
                .ret-stat-card { flex: 1; min-width: 120px; border-radius: 14px; padding: 14px 18px; background: #fff; box-shadow: 0 4px 16px rgba(0,0,0,0.06); border-left: 4px solid transparent; }
                .ret-filter { background: #fff; border-radius: 16px; padding: 16px 20px; margin-bottom: 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.05); }
                .ret-filter label { font-size: 11px; font-weight: 600; color: #888; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px; }
                .ret-filter .form-control { border-radius: 10px; border: 1.5px solid #eee; font-size: 13px; padding: 7px 11px; }
                .ret-filter .form-control:focus { border-color: #4e9191; box-shadow: 0 0 0 3px rgba(78,145,145,0.1); }
                .order-num { font-size: 12px !important; font-weight: 700 !important; color: #4e9191 !important; font-family: monospace !important; }
                .ret-search-wrap { position: relative; margin-bottom: 14px; }
                .ret-search-wrap input { border-radius: 12px; border: 1.5px solid #eee; padding: 9px 14px 9px 38px; font-size: 13px; width: 100%; outline: none; }
                .ret-search-wrap input:focus { border-color: #4e9191; box-shadow: 0 0 0 3px rgba(78,145,145,0.1); }
                .ret-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aaa; font-size: 14px; }
                .ret-table-card { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); overflow: visible; margin-bottom: 50px; }
                .ret-table-card .table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                .ret-table-card table { margin: 0; min-width: 1300px; }
                .ret-table-card thead th { background: #f8f9fa; font-size: 11px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; padding: 13px 12px; border-bottom: 2px solid #eee; white-space: nowrap; }
                .ret-table-card tbody tr:hover { background: #f8fffe !important; }
                .ret-table-card tbody td { font-size: 13px; color: #444; padding: 11px 12px; vertical-align: middle; white-space: nowrap; }
                .status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 6px; }
                .round-pagination .page-item.actives .page-link { background: #4e9191 !important; border-color: #4e9191 !important; color: #fff !important; border-radius: 8px !important; }
                .round-pagination .page-item .page-link { background: #fff; border: 1.5px solid #eee; color: #555; border-radius: 8px !important; margin: 0 2px; font-size: 12px; font-weight: 600; padding: 5px 12px; transition: all 0.2s; box-shadow: none !important; }
                .round-pagination .page-item .page-link:hover { background: #4e9191 !important; color: #fff !important; }
                .product-cell { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; color: #555; }
            `}</style>

            <ContentWrapper>
                <div className="retention-page">

                    {/* Breadcrumb */}
                    <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                        <nav><ol className="breadcrumb mb-0 p-0">
                            <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                            <li className="breadcrumb-item">Retention</li>
                        </ol></nav>
                    </div>

                    {/* Stats */}
                    <div className="ret-stats">
                        {[
                            { label: 'Total Customers', value: stats.total, color: '#4e9191', border: '#4e9191' },
                            { label: 'Active', value: stats.active, color: '#0e7c52', border: '#0e7c52' },
                            { label: 'Ending Soon', value: stats.endingSoon, color: '#e65100', border: '#ff9800' },
                            { label: 'Drop-off', value: stats.dropoff, color: '#c62828', border: '#c62828' },
                            { label: 'Loyal / VIP', value: stats.vip, color: '#b8860b', border: '#f9a825' },
                            { label: 'Total LTV', value: `₹ ${Math.round(stats.totalLTV).toLocaleString()}`, color: '#1565c0', border: '#1565c0' },
                        ].map((s, i) => (
                            <div key={i} className="ret-stat-card" style={{ borderLeftColor: s.border }}>
                                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Filters */}
                    <div className="ret-filter">
                        <form ref={ref} onSubmit={handleFilter}>
                            <div className="row g-2 align-items-end">

                                {/* Purchase Stage */}
                                <div className="col-12 col-lg-2">
                                    <label>Purchase Stage</label>
                                    <select name="purchase_stage" className="form-control" onChange={handleInput}
                                        defaultValue={filters.purchase_stage || ''}>
                                        <option value="">All Stages</option>
                                        <option>First Time Buyer</option>
                                        <option>Early Repeat</option>
                                        <option>Consistent</option>
                                        <option>Loyal/VIP</option>
                                        <option>No Delivery</option>
                                    </select>
                                </div>

                                {/* Treatment Status */}
                                <div className="col-12 col-lg-2">
                                    <label>Treatment Status</label>
                                    <select name="treatment_status" className="form-control" onChange={handleInput}
                                        defaultValue={filters.treatment_status || ''}>
                                        <option value="">All Status</option>
                                        <option>Active</option>
                                        <option>Ending Soon</option>
                                        <option>Drop-off</option>
                                    </select>
                                </div>

                                {/* Health Category */}
                                <div className="col-12 col-lg-2">
                                    <label>Health Category</label>
                                    <select
                                        name="health_category"
                                        className="form-control"
                                        onChange={handleInput}
                                        defaultValue={filters.health_category || ''}
                                    >
                                        <option value="">All Categories</option>
                                        <option value="Joint Pain">Joint Pain</option>
                                        <option value="Diabetes">Diabetes</option>
                                        <option value="Addiction">Addiction</option>
                                        <option value="Sexual Health">Sexual Health</option>
                                        <option value="Immunity">Immunity</option>
                                        <option value="Piles Management">Piles Management</option>
                                        <option value="Hair Care">Hair Care</option>
                                        <option value="Digestion">Digestion</option>
                                        <option value="Weight Management">Weight Management</option>
                                        <option value="Others">Others</option>
                                    </select>
                                </div>

                                {/* ✅ NEW: Delivery Status */}
                                <div className="col-12 col-lg-2">
                                    <label>Delivery Status</label>
                                    <select name="delivery_status" className="form-control" onChange={handleInput}
                                        defaultValue={filters.delivery_status || ''}>
                                        <option value="">All Orders</option>
                                        <option value="delivered">Delivered Only</option>
                                        <option value="not_delivered">Not Delivered</option>
                                    </select>
                                </div>

                                {/* State */}
                                <div className="col-12 col-lg-2">
                                    <label>State / Region</label>
                                    <input name="state" className="form-control" type="text"
                                        placeholder="e.g. Delhi" onChange={handleInput}
                                        defaultValue={filters.state || ''} />
                                </div>

                                {/* Language */}
                                <div className="col-12 col-lg-2">
                                    <label>Language</label>
                                    <select name="language" className="form-control" onChange={handleInput}
                                        defaultValue={filters.language || ''}>
                                        <option value="">All Languages</option>
                                        <option>Hindi</option>
                                        <option>Marathi</option>
                                        <option>Gujarati</option>
                                        <option>Bengali</option>
                                        <option>Tamil</option>
                                        <option>Telugu</option>
                                        <option>Kannada</option>
                                        <option>Malayalam</option>
                                        <option>Punjabi</option>
                                    </select>
                                </div>

                                {/* ✅ NEW: Date Range */}
                                <div className="col-12 col-lg-2">
                                    <label>Date From</label>
                                    <input name="date_from" className="form-control" type="date"
                                        onChange={handleInput} defaultValue={filters.date_from || ''} />
                                </div>
                                <div className="col-12 col-lg-2">
                                    <label>Date To</label>
                                    <input name="date_to" className="form-control" type="date"
                                        onChange={handleInput} defaultValue={filters.date_to || ''} />
                                </div>

                                {/* Buttons */}
                                <div className="col-12 col-lg-2">
                                    <div className="d-flex gap-2 align-items-center" style={{ paddingTop: 22 }}>
                                        <button type="submit" className="btn btn-sm"
                                            style={{ background: '#4e9191', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 14px', whiteSpace: 'nowrap' }}>
                                            <FaFilter style={{ marginRight: 4, fontSize: 10 }} /> Filter
                                        </button>
                                        <button type="button" onClick={reset} className="btn btn-sm"
                                            style={{ background: '#fff3cd', color: '#856404', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 10px' }}>
                                            <FaRedo style={{ fontSize: 11 }} />
                                        </button>
                                        <CsvDownloader filename="Retention" extension=".csv" datas={filtered} columns={csvColumns}>
                                            <button type="button" className="btn btn-sm"
                                                style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 10px' }}>
                                                <CiExport style={{ fontSize: 14 }} />
                                            </button>
                                        </CsvDownloader>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Search */}
                    <div className="ret-search-wrap">
                        <FaSearch className="ret-search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name, order number or mobile..."
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}
                        />
                    </div>

                    {/* Table */}
                    <div className="ret-table-card">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                            <span style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>
                                Showing <strong>{records.length}</strong> of <strong>{filtered.length}</strong> customers
                            </span>
                            <span style={{ fontSize: 13, color: '#666' }}>Page <strong>{currentPage}</strong> of <strong>{npage || 1}</strong></span>
                        </div>

                        <div className="table-scroll">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Order No.</th>
                                        <th>Customer</th>
                                        <th>Language</th>
                                        <th>Health Category</th>
                                        <th>Product Purchased</th>
                                        <th>Pack</th>
                                        <th>Orders</th>
                                        <th>Delivered</th>
                                        <th>LTV</th>
                                        <th>Last Order</th>
                                        <th>Refill Date</th>
                                        <th>Status</th>
                                        <th>Stage</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading
                                        ? [...Array(10)].map((_, i) => <SkeletonRow key={i} />)
                                        : records.length === 0
                                            ? <tr><td colSpan={15} className="text-center py-5" style={{ color: '#bbb' }}>No customers found</td></tr>
                                            : records.map((r, i) => {
                                                const ss = STATUS_STYLE[r.treatment_status] || STATUS_STYLE['Active'];
                                                const ps = STAGE_STYLE[r.purchase_stage] || STAGE_STYLE['First Time Buyer'];
                                                return (
                                                    <tr key={i} style={{
                                                        background: r.treatment_status === 'Ending Soon'
                                                            ? 'linear-gradient(90deg, #fff8f0 0%, #fff 100%)'
                                                            : r.treatment_status === 'Drop-off'
                                                                ? 'linear-gradient(90deg, #fff5f5 0%, #fff 100%)'
                                                                : 'transparent',
                                                        borderLeft: r.treatment_status === 'Ending Soon'
                                                            ? '3px solid #ff9800'
                                                            : r.treatment_status === 'Drop-off'
                                                                ? '3px solid #c62828'
                                                                : '3px solid transparent',
                                                    }}>
                                                        <td style={{ color: '#aaa', fontSize: 12 }}>{(currentPage - 1) * perPage + i + 1}</td>
                                                        <td className="order-num">#{r.last_order_number}</td>
                                                        <td>
                                                            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name || '—'}</div>
                                                            <div style={{ fontSize: 11, color: '#888' }}>{r.mobile}</div>
                                                            <div style={{ fontSize: 10, color: '#4e9191' }}>{r.state}</div>
                                                        </td>
                                                        <td>
                                                            <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                                                                {r.language}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 12, color: '#555' }}>
                                                            {getCategoryByProduct(r.last_product, r.last_variant)}
                                                        </td>

                                                        {/* Product Purchased column */}
                                                        <td>
                                                            <div className="product-cell" title={r.last_product}>
                                                                {r.last_product || '—'}
                                                            </div>
                                                            {r.last_variant && r.last_variant !== '-' && (
                                                                <div style={{ fontSize: 10, color: '#6a1b9a', marginTop: 2 }}>
                                                                    {r.last_variant}
                                                                </div>
                                                            )}
                                                        </td>

                                                        <td>
                                                            <span style={{ background: '#f3e5f5', color: '#6a1b9a', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                                                                {r.pack_label}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontWeight: 700, color: '#4e9191', textAlign: 'center' }}>{r.order_count}</td>

                                                        {/* ✅ NEW: Delivered count */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <span style={{
                                                                background: (r.successful_orders || 0) > 0 ? '#e8f5e9' : '#ffebee',
                                                                color: (r.successful_orders || 0) > 0 ? '#0e7c52' : '#c62828',
                                                                padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700
                                                            }}>
                                                                {r.successful_orders || 0}
                                                            </span>
                                                        </td>

                                                        <td style={{ fontWeight: 600, color: '#0e7c52' }}>{numberFormat(r.ltv)}</td>
                                                        <td style={{ fontSize: 12 }}>{r.last_order_date}</td>

                                                        {/* Refill date + days badge */}
                                                        <td>
                                                            <span style={{ fontWeight: 600, fontSize: 12, color: ss.color }}>
                                                                {r.refill_date}
                                                            </span>
                                                            <div style={{ marginTop: 3 }}>
                                                                {r.days_remaining <= 0 ? (
                                                                    <span style={{ background: '#ffebee', color: '#c62828', padding: '1px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, display: 'inline-block' }}>
                                                                        🔴 Refill Overdue
                                                                    </span>
                                                                ) : r.days_remaining <= 7 ? (
                                                                    <span style={{ background: '#fff3e0', color: '#e65100', padding: '1px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, display: 'inline-block', animation: 'pulse 1.5s infinite' }}>
                                                                        ⚡ Refill in {r.days_remaining}d
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ background: '#e8f5e9', color: '#0e7c52', padding: '1px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, display: 'inline-block' }}>
                                                                        ✓ {r.days_remaining}d remaining
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <span style={{ background: ss.bg, color: ss.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}>
                                                                <span className="status-dot" style={{ background: ss.dot }} />
                                                                {r.treatment_status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span style={{ background: ps.bg, color: ps.color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                                                                {r.purchase_stage}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Link to={`/retention-customer/${r.mobile}`}
                                                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, background: '#e8f5e9', color: '#0e7c52', textDecoration: 'none' }}>
                                                                <FaEye style={{ fontSize: 13 }} />
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                    }
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="d-flex justify-content-end p-3">
                            <nav>
                                <ul className="pagination round-pagination mb-0">
                                    <li className="page-item">
                                        <a href="#" className="page-link" onClick={e => { e.preventDefault(); if (currentPage > 1) setPage(currentPage - 1); }}>← Prev</a>
                                    </li>
                                    {numbers.slice(Math.max(0, currentPage - 2), Math.max(5, currentPage + 3)).map((n, i) => (
                                        <li key={i} className={`page-item ${currentPage === n ? 'actives' : ''}`}>
                                            <a href="#" className="page-link" onClick={e => { e.preventDefault(); setPage(n); }}>{n}</a>
                                        </li>
                                    ))}
                                    <li className="page-item">
                                        <a href="#" className="page-link" onClick={e => { e.preventDefault(); if (currentPage < npage) setPage(currentPage + 1); }}>Next →</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(RetentionPage);
